import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Sector } from 'recharts';
import type { PieSectorShapeProps } from 'recharts';
import type { LabeledPieDonutViz } from 'shared/src/api/ai-labeled-viz-types';
import type { ValueFormat } from 'shared/src/api/ai-viz-types';
import { transformPieDonut, formatVizValue } from 'shared/src/utils/ai/ai-viz-transformers';
import type { CubeRow } from './chart-utils';
import { CHART_PALETTE, TOOLTIP_STYLE } from './chart-utils';

type PieDonutChartProps = {
  rows: CubeRow[];
  viz: LabeledPieDonutViz;
};

type ValidPieData = { name: string; value: number };

export function PieDonutChart({ rows, viz }: PieDonutChartProps) {
  const raw = transformPieDonut(rows, viz);
  const valueFormat = viz.labels.valueFormat;

  // Filter to valid positive values
  const data: ValidPieData[] = raw.filter(
    (d): d is ValidPieData => d.value !== null && d.value > 0
  );

  if (data.length === 0) {
    return (
      <div className="w-full bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>
        <div className="flex items-center justify-center" style={{ height: 450 }}>
          <p className="text-gray-400 text-lg">No data available</p>
        </div>
      </div>
    );
  }

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>

      <ResponsiveContainer width="100%" height={450}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
            minAngle={2}
            shape={(props: PieSectorShapeProps) => renderActiveShape(props)}>
            {data.map((_entry, i) => (
              <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
            ))}
          </Pie>

          <Tooltip
            content={<CustomTooltip total={total} valueFormat={valueFormat} />}
            cursor={false}
            isAnimationActive={false}
          />

          <Legend wrapperStyle={{ paddingTop: 6, fontSize: 12 }} iconSize={10} />

          {/* Center label */}
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 22, fontWeight: 700, fill: '#111827' }}>
            <tspan x="50%" dy="-0.5em">
              {formatVizValue(total, valueFormat)}
            </tspan>
            <tspan x="50%" dy="1.6em" style={{ fontSize: 13, fontWeight: 400, fill: '#6b7280' }}>
              {viz.labels.valueLabel}
            </tspan>
          </text>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Internal helpers (in call order) ───

function renderActiveShape(props: PieSectorShapeProps): React.ReactElement {
  const { isActive, outerRadius = 0, fill, ...rest } = props;

  if (!isActive) {
    return <Sector {...rest} outerRadius={outerRadius} fill={fill} />;
  }

  return (
    <g>
      <Sector {...rest} outerRadius={outerRadius + 6} fill={fill} />
      <Sector
        {...rest}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 11}
        fill={fill}
        opacity={0.3}
      />
    </g>
  );
}

function CustomTooltip({
  active,
  payload,
  total,
  valueFormat
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: ValidPieData }>;
  total: number;
  valueFormat: ValueFormat;
}) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';

  return (
    <div style={TOOLTIP_STYLE}>
      <p style={{ marginBottom: 4, fontWeight: 600 }}>{entry.name}</p>
      <p style={{ margin: 0, paddingTop: 2, paddingBottom: 2 }}>
        {formatVizValue(entry.value, valueFormat)} &middot; {pct}% of total
      </p>
    </div>
  );
}
